//import { captureException } from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { errorToast } from '../../../../../helpers/toastHelper';
import { setCuratorNotSaved } from '../../../../../pages/admin/curator/curatorState';
import {
  setSelectedHDRI,
  curatorLightActions,
  ENVIRONMENT_LIGHT,
  setEnvironmentColor,
  setHDRIRotation,
  setEyeToolActive,
  selectDefaultEnvironmentLightData,
  setPreviewHDRI,
  selectEnvironmentLightType,
  selectEnvironmentColor,
  selectSelectedHDRI,
  selectEyeToolActive,
  selectHDRIRotation,
  selectHDRIIntensity,
  setAO,
} from '../../../../../redux/slicers/admin/curatorLightSlicer';
import { getAppState } from '../../../../../redux/store';
import { useUnityContext } from '../../../../container/unityContainer';
import { undoRedo } from '../../UndoRedo/UndoRedo';
import { isRetailAutomation, isRetailConfigurator } from '../../../../Configurator/utils/index';
import { LIGHT_API } from '../../../../../config/constant/apiConstant';
import { 
  RETAIL_HDRI_LIGHT_ID_PRODUCTION, 
  RETAIL_HDRI_LIGHT_ID_NON_PRODUCTION 
} from '../../../../../config/constant/projectConstant';
import { apiGet } from '../../../../../helpers/api';
import { isProductionUrl } from '../../../../../helpers/urlHelper';

export const useUnityHDRI = () => {
  const dispatch = useDispatch();
  const unityContext = useUnityContext();
  const defaultEnvironmentLightData = useSelector(selectDefaultEnvironmentLightData);

  const unitySelectHDRI = async (hdri) => {
    const isRetailAutomationPage = isRetailAutomation();
    const isRetailConfiguratorPageCheck = isRetailConfigurator();
    let hdriUrl = null;
    let hdriRotation = { x: 0, y: 0, z: 0 };
    let hdriIntensity = 1;
    let hdrivisible = true;

     if (isRetailAutomationPage || isRetailConfiguratorPageCheck) {
      try {
        // Fetch HDRI data from API for retail automation/configurator
        const hdriLightId = isProductionUrl ? RETAIL_HDRI_LIGHT_ID_PRODUCTION : RETAIL_HDRI_LIGHT_ID_NON_PRODUCTION;
        const hdriData = await apiGet(`${LIGHT_API}${hdriLightId}/`);
        if (hdriData && hdriData.unity_file) {
          hdriUrl = hdriData.unity_file;
        }        
        // Use static values for rotation and intensity in retail automation/configurator
        hdriRotation = { x: 0, y: 0, z: 0 };
        hdriIntensity = 1;
        hdrivisible = false;
      } catch (error) {
        console.error('Error fetching HDRI data for retail automation/configurator:', error);
        // Fall back to existing values if API call fails
        return;
      }
    } else if (hdri) {
      hdriUrl = hdri.unity_file;
      hdriRotation = selectHDRIRotation(getAppState());
      hdriIntensity = selectHDRIIntensity(getAppState());
      hdrivisible = true;
    }
    if (!hdriUrl) return;
    unityContext.lightModule.UpdateEnvironmentSkyboxReceiver({
      url: hdriUrl,
      rotation: hdriRotation,
      intensity: hdriIntensity,
      visibleHdri: hdrivisible,
    });
  };

  const selectHDRI = (hdri, { skipHistory } = {}) => {
    if (!skipHistory) {
      undoRedo.environmentLight.applyHdri({ nextHDRI: hdri });
    }
    dispatch(curatorLightActions.setHDRIChange(true));
    dispatch(setSelectedHDRI(hdri));
    dispatch(setPreviewHDRI(null));
    dispatch(setEyeToolActive(true));
    unitySelectHDRI(hdri);
    setCuratorNotSaved();
  };

  const updateColorIntesity = (intensity) => {
    dispatch(curatorLightActions.setEnvironmentColorIntensity(intensity));
    unityContext.lightModule.UpdateColorIntensityReceiver(intensity);
    setCuratorNotSaved();
  };

  const updateHDRIIntesity = (intensity) => {
    dispatch(curatorLightActions.setHDRIIntensityChange(true));
    dispatch(curatorLightActions.setHDRIIntensity(intensity));
    unityContext.lightModule.UpdateSkyboxIntensityReceiver(intensity);
    setCuratorNotSaved();
  };

  const updateColor = (color) => {
    unityContext.lightModule.UpdateEnvironmentColorReceiver(color);
    dispatch(setEnvironmentColor(color));
    setCuratorNotSaved();
  };

  const updateRotation = (rotation) => {
    dispatch(curatorLightActions.setHDRIRotationChange(true));
    unityContext.lightModule.UpdateSkyboxRotationReceiver(JSON.stringify(rotation));
    dispatch(setHDRIRotation(rotation));
    setCuratorNotSaved();
  };

  const updateAO = (ao) => {
    dispatch(setAO(ao));
    setCuratorNotSaved();
  };

  const toggleEnvironmentLightType = (type, { skipHistory } = {}) => {
    const environmentLightType = selectEnvironmentLightType(getAppState());
    if (environmentLightType === type) {
      // one environment type should always be enabled
      type = type === ENVIRONMENT_LIGHT.HDRI ? ENVIRONMENT_LIGHT.COLOR : ENVIRONMENT_LIGHT.HDRI;
    }

    // save data to history
    if (!skipHistory) {
      undoRedo.environmentLight.toggleLightType({ nextType: type });
    }

    // update states
    dispatch(curatorLightActions.setEnvironmentLightType(type));

    if (type === ENVIRONMENT_LIGHT.HDRI) {
      dispatch(setEyeToolActive(true));
      const selectedHDRI = selectSelectedHDRI(getAppState());
      if (!selectedHDRI) return;
      unitySelectHDRI(selectedHDRI);
    } else if (type === ENVIRONMENT_LIGHT.COLOR) {
      const envColor = selectEnvironmentColor(getAppState());
      unityContext.lightModule.UpdateEnvironmentColorReceiver(envColor);
    } else {
      errorToast('Invalid environment light type ' + type);
      //captureException(new Error('Invalid environment light type ' + type));
    }

    setCuratorNotSaved();
  };

  const toggleHDRIEyeTool = (active) => {
    try {
      const eyeToolActive = selectEyeToolActive(getAppState());
      const nextState = typeof active === 'boolean' ? active : !eyeToolActive;
      unityContext.lightModule.EnableDisableHDRSkyboxReceiver(nextState);
      dispatch(setEyeToolActive(nextState));
    } catch (e) {
      //captureException(e);
      console.error(e);
    }
  };

  const resetLight = ({ skipHistory } = {}) => {
    try {
      const defaultEnvironmentLightData = selectDefaultEnvironmentLightData(getAppState());

      const { hdri, color } = defaultEnvironmentLightData || {};
      const selectedLightType = selectEnvironmentLightType(getAppState());

      if (!skipHistory) {
        undoRedo.environmentLight.resetLight();
      }

      if (hdri) {
        if (hdri.enabled && selectedLightType !== ENVIRONMENT_LIGHT.HDRI) {
          toggleEnvironmentLightType(ENVIRONMENT_LIGHT.HDRI, { skipHistory: true });
        }

        dispatch(setSelectedHDRI(hdri.data));
        updateHDRIIntesity(hdri.intensity);
        updateRotation(hdri.rotation);
        dispatch(curatorLightActions.setHDRIIntensityChange(false));
        dispatch(curatorLightActions.setHDRIChange(false));
        dispatch(curatorLightActions.setHDRIRotationChange(false));
      }

      if (color) {
        if (color.enabled && selectedLightType !== ENVIRONMENT_LIGHT.COLOR) {
          toggleEnvironmentLightType(ENVIRONMENT_LIGHT.COLOR, { skipHistory: true });
        }

        updateColor(color.color);
        updateColorIntesity(color.intensity);
      }

      setCuratorNotSaved();
    } catch (e) {
      //captureException(e);
      console.error(e);
    }
  };

  const resetLightEnabled =
    Boolean(defaultEnvironmentLightData?.hdri) || Boolean(defaultEnvironmentLightData?.color);

  return {
    selectHDRI,
    resetLightEnabled,
    toggleEnvironmentLightType,
    updateColorIntesity,
    updateHDRIIntesity,
    updateColor,
    updateRotation,
    toggleHDRIEyeTool,
    resetLight,
    unitySelectHDRI,
    updateAO,
  };
};
